import { CONTACT_METHODS, ENQUIRY_TYPES, SUPPLIER_CATEGORIES } from '@/data/site';

/**
 * Static form definitions for Netlify's build-time form detection.
 *
 * Netlify only registers forms that exist in the deployed static HTML, so this
 * component renders a hidden, non-interactive copy of every form's field names
 * and the required `form-name` field. The resulting markup is what Netlify scans.
 *
 * NOTE FOR EXPORT: because this is a client-rendered SPA, ensure these
 * definitions are present in a static HTML file in the export (for example by
 * copying the rendered markup into the built index.html, or a plain HTML file),
 * then set each live form's action to "/" and remove the Readdy fetch so it
 * posts natively to Netlify. See the handover notes in the summary.
 */
export default function NetlifyForms() {
  const text = (name: string) => <input key={name} type="text" name={name} />;
  const email = <input type="email" name="email" />;
  const tel = <input type="tel" name="telephone" />;
  const hidden = (value: string) => <input type="hidden" name="form-name" value={value} />;

  const customerFields = (
    <>
      <input type="hidden" name="form-name" value="" />
      {text('first_name')}
      {text('last_name')}
      {text('business_name')}
      {email}
      {tel}
    </>
  );

  return (
    <div className="form-honeypot" aria-hidden="true">
      {/* contact_enquiry */}
      <form name="contact_enquiry" data-netlify="true" hidden>
        {hidden('contact_enquiry')}
        {customerFields}
        {text('enquiry_type')}
        <textarea name="message"></textarea>
        <input type="checkbox" name="privacy_ack" />
        <input type="checkbox" name="marketing_consent" />
      </form>

      {/* personal_match */}
      <form name="personal_match" data-netlify="true" hidden>
        {hidden('personal_match')}
        {customerFields}
        <select name="preferred_contact_method">
          {CONTACT_METHODS.map((option) => (
            <option key={option} value={option} />
          ))}
        </select>
        {text('event_date')}
        {text('event_location')}
        {text('supplier_budget')}
        <select name="supplier_category">
          {SUPPLIER_CATEGORIES.map((category) => (
            <option key={category} value={category} />
          ))}
        </select>
        {text('supplier_category_other')}
        {text('event_type')}
        {text('event_type_other')}
        <textarea name="requirements"></textarea>
        <textarea name="additional_info"></textarea>
        <input type="checkbox" name="privacy_ack" />
        <input type="checkbox" name="marketing_consent" />
      </form>

      {/* event_rescue */}
      <form name="event_rescue" data-netlify="true" hidden>
        {hidden('event_rescue')}
        {customerFields}
        <select name="preferred_contact_method">
          {CONTACT_METHODS.map((option) => (
            <option key={option} value={option} />
          ))}
        </select>
        {text('event_date')}
        {text('event_location')}
        {text('supplier_budget')}
        <select name="supplier_category">
          {SUPPLIER_CATEGORIES.map((category) => (
            <option key={category} value={category} />
          ))}
        </select>
        {text('supplier_category_other')}
        {text('event_type')}
        {text('event_type_other')}
        <textarea name="requirements"></textarea>
        {text('deadline')}
        {text('flexibility')}
        <textarea name="what_have_you_tried"></textarea>
        <textarea name="why_urgent"></textarea>
        <textarea name="additional_info"></textarea>
        <input type="checkbox" name="privacy_ack" />
        <input type="checkbox" name="marketing_consent" />
      </form>

      {/* event_dream_team */}
      <form name="event_dream_team" data-netlify="true" hidden>
        {hidden('event_dream_team')}
        {customerFields}
        <select name="preferred_contact_method">
          {CONTACT_METHODS.map((option) => (
            <option key={option} value={option} />
          ))}
        </select>
        {text('event_date')}
        {text('event_location')}
        {text('supplier_budget')}
        {SUPPLIER_CATEGORIES.map((category) => (
          <input key={category} type="checkbox" name="supplier_categories" value={category} />
        ))}
        {text('supplier_category_other')}
        {text('event_type')}
        {text('event_type_other')}
        <textarea name="requirements"></textarea>
        {text('guest_numbers')}
        {text('overall_budget')}
        {text('style_theme')}
        {text('flexibility')}
        <textarea name="suppliers_booked"></textarea>
        <textarea name="special_needs"></textarea>
        <textarea name="additional_info"></textarea>
        <input type="checkbox" name="privacy_ack" />
        <input type="checkbox" name="marketing_consent" />
      </form>

      {/* supplier_application */}
      <form name="supplier_application" data-netlify="true" hidden>
        {hidden('supplier_application')}
        {text('business_name')}
        {text('first_name')}
        {text('last_name')}
        {email}
        {tel}
        {text('website_social')}
        {SUPPLIER_CATEGORIES.map((category) => (
          <input key={category} type="checkbox" name="supplier_categories" value={category} />
        ))}
        {text('supplier_category_other')}
        <textarea name="services_offered"></textarea>
        {text('coverage_area')}
        <textarea name="experience"></textarea>
        <textarea name="insurance_qualifications"></textarea>
        {text('indicative_pricing')}
        <textarea name="additional_info"></textarea>
        <input type="checkbox" name="privacy_ack" />
        <input type="checkbox" name="marketing_consent" />
      </form>

      {/* supplier_insights */}
      <form name="supplier_insights" data-netlify="true" hidden>
        {hidden('supplier_insights')}
        {SUPPLIER_CATEGORIES.map((category) => (
          <input key={category} type="checkbox" name="supplier_categories" value={category} />
        ))}
        {text('supplier_category_other')}
        {text('q2_ideal_customer')}
        {text('q3_booking_value')}
        {text('q4_enquiry_source')}
        {text('q5_wasted_time')}
        {text('q6_referrals')}
        {text('q7_pays_commission')}
        {text('q7b_commission_percentage')}
        {text('q8_success_fee')}
        {text('q9_value_to_pay')}
        {text('q10_growth_challenge')}
        {text('first_name')}
        {text('last_name')}
        {text('business_name')}
        {email}
        <input type="checkbox" name="marketing_consent" />
      </form>

      {/* customer_checklist */}
      <form name="customer_checklist" data-netlify="true" hidden>
        {hidden('customer_checklist')}
        {text('first_name')}
        {text('last_name')}
        {email}
        <input type="checkbox" name="marketing_consent" />
        <input type="checkbox" name="privacy_ack" />
      </form>

      {/* Static form-name source for the general enquiry dropdown options */}
      <span data-enquiry-types={ENQUIRY_TYPES.join('|')} />
    </div>
  );
}